import React from 'react'
import { observer } from 'mobx-react'
import cn from 'classnames'
import { RowNode, ICellRendererParams } from 'ag-grid-community'

import Field from 'stores/field/Field'
import Record from 'stores/record/Record'

import RelationPill from './RelationPill'

import styles from './tableCellRenderer.scss'

interface IProps extends ICellRendererParams {
  node: ICellRendererParams['node']
  field: Field
  resizeRowForRelation: (node: RowNode, field: Field) => void
}

class TableCellRenderer extends React.Component<IProps> {
  handleClickRelation = () => {
    const { api: gridApi, node, field } = this.props
    // Resize row to accommodate relation table
    this.props.resizeRowForRelation(node, field)

    // Then start editing the cell
    const cell = gridApi.getFocusedCell()
    gridApi.startEditingCell({
      rowIndex: cell.rowIndex, // Own rowIndex is not dependable, because there may be uncommitted records. They are reordered manually, so rowIndices are not what appears on screen.
      colKey: cell.column,
    })

    // The order of scroll + editing is important because relation cells are positioned absolutely (by the grid). We want to make sure this position calculation happens AFTER the cell is in its final position (scroll-wise)
  }

  getRenderedValue = (): string | null => {
    const {
      node: { data },
      field,
    } = this.props
    const record: Record = data
    const value = record.value[field.name]

    if (!record) {
      return ''
    }

    if (field.isRelation) {
      // We'll render the relation pill in this case, so render nothing else
      return null
    }

    if (field.isJson) {
      // Always JSON.stringify for JSON values, returning `null` if the value does not exist
      return JSON.stringify(value || null)
    }

    if (value === undefined) {
      return field.defaultAsString
    }

    if (value === null) {
      return 'null'
    }

    if (field.isBigInt) {
      if (field.isList) {
        return '[' + value.map((v: bigint) => v.toString()).join(', ') + ']'
      }
      return value.toString()
    }

    if (field.isBytes) {
      if (field.isList) {
        return (
          '[' +
          value
            .map(
              (v: Buffer | Uint8Array) =>
                (v instanceof Uint8Array ? Buffer.from(v) : v).toString(
                  'base64'
                ) // Can be Uin8Array when restored from IndexedDB
            )
            .join(', ') +
          ']'
        )
      }
      return (value instanceof Uint8Array // Can be Uin8Array when restored from IndexedDB
        ? Buffer.from(value)
        : value
      ).toString('base64')
    }

    if (field.isList) {
      // For a list value, JSON.stringify the value
      return JSON.stringify(value)
    } else {
      // For scalars and enums, just make sure the value is a string
      return String(value)
    }
  }

  render() {
    const {
      node: { data },
      field,
    } = this.props
    const record: Record = data

    if (!record) {
      return <div className={styles.loading}></div>
    }

    const value = record.value[field.name]

    const isUndefined = value === undefined // `value === undefined` represents a cell in a new record, with no actions applied on it. We will display the @default value from the schema (if it exists)
    const isNull = value === null // `value === null` represents actual null values in the DB

    return (
      <div
        className={cn(styles.container, {
          [styles.placeholder]: isUndefined || isNull,
          [styles.relation]: field.isRelation,
        })}
      >
        {field.isRelation && (
          <RelationPill
            type={field.type}
            isList={field.isList}
            isNull={!value}
            count={(value || []).length}
            onClick={this.handleClickRelation}
          />
        )}

        {this.getRenderedValue()}
      </div>
    )
  }
}

export default observer(TableCellRenderer)
